import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Grid,
  Chip,
  alpha,
  useTheme,
} from "@mui/material";
import {
  LocalShipping as LocalShippingIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

/**
 * Dialog for receiving OUTPUT items
 *
 * Props:
 * - open: boolean
 * - onClose: function
 * - lineItem: The OUTPUT line item being received
 * - onReceive: function(receiveData) - callback with { lineItemId, receivedQty, receivedBy, notes }
 */
function ReceiveOutputDialog({
  subOrderPerson,
  open,
  onClose,
  lineItem,
  onReceive,
}) {
  const theme = useTheme();
  const [receivedQty, setReceivedQty] = useState("");
  const [receivedBy, setReceivedBy] = useState(subOrderPerson);
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");

  // Remaining quantity still expected for this line item (ordered minus already received).
  // Falls back to the full ordered qty if the caller didn't supply progress data.
  const maxReceivableQty = lineItem?.remainingQty ?? lineItem?.qty ?? 0;

  const handleReceive = () => {
    // Validation
    if (!receivedQty || parseInt(receivedQty, 10) <= 0) {
      setError("Received quantity must be greater than 0");
      return;
    }

    if (parseInt(receivedQty, 10) > maxReceivableQty) {
      setError(
        `Received quantity cannot exceed the remaining expected quantity (${maxReceivableQty} ${lineItem.orderUnit})`
      );
      return;
    }

    if (!receivedBy.trim()) {
      setError("Please enter who received the items");
      return;
    }

    // Call the onReceive callback
    onReceive({
      lineItemId: lineItem.lineId,
      receivedQty: parseInt(receivedQty, 10),
      receivedBy: receivedBy.trim(),
      notes: notes.trim(),
      reconstructionId: null, // Not a reconstruction receive
    });

    // Reset form
    handleClose();
  };

  const handleClose = () => {
    setReceivedQty("");
    setReceivedBy("");
    setNotes("");
    setError("");
    onClose();
  };

  if (!lineItem) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LocalShippingIcon color="primary" />
          <Typography variant="h6">Receive Output Items</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Item Information */}
        <Box
          sx={{
            mb: 3,
            p: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            borderRadius: 2,
          }}
        >
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Item Details
          </Typography>
          <Typography variant="body1" fontWeight="600">
            {lineItem.item}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
            <Chip
              label={`Brand: ${lineItem.brand}`}
              size="small"
              variant="outlined"
            />
            <Chip
              label={`Ordered: ${lineItem.qty} ${lineItem.orderUnit}`}
              size="small"
              variant="outlined"
              color="primary"
            />
            <Chip
              label={`Remaining: ${maxReceivableQty} ${lineItem.orderUnit}`}
              size="small"
              variant="outlined"
              color={maxReceivableQty > 0 ? "success" : "default"}
            />
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              type="number"
              label="Received Quantity"
              value={receivedQty}
              onChange={(e) =>
                setReceivedQty(e.target.value.replace(/[^0-9]/g, ""))
              }
              onWheel={(e) => e.target.blur()}
              error={parseInt(receivedQty, 10) > maxReceivableQty}
              InputProps={{
                inputProps: { min: 0, max: maxReceivableQty, step: 1 },
              }}
              helperText={`Enter quantity in ${lineItem.orderUnit} (max ${maxReceivableQty} remaining)`}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Received By"
              value={receivedBy}
              onChange={(e) => setReceivedBy(e.target.value)}
              placeholder="Enter name of person who received"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes (Optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Batch number, production date, or other notes..."
            />
          </Grid>
        </Grid>

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="caption">
            <strong>Note:</strong> Receiving only records the quantity.
            Inventory will be updated after quality checking (good vs damaged).
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} startIcon={<CloseIcon />}>
          Cancel
        </Button>
        <Button
          onClick={handleReceive}
          variant="contained"
          color="primary"
          startIcon={<LocalShippingIcon />}
        >
          Receive Items
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ReceiveOutputDialog;
